import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import SolutionDetailCard from './SolutionDetailCard';
import { ourSolutionData } from '../../utils/ourSolutionData';
import { client } from '../../sanity/client';
import PageHero from '../common/PageHero';
import SEO from '../SEO/SEO';
import { BreadcrumbSchema } from '../SEO/StructuredData';

type SanitySolution = {
  _id: string;
  slug: string;
  language?: string;
  headingPairs?: { label: string; anchor: string }[];
  body?: any[];
};

const slugify = (value: string) =>
  encodeURIComponent(
    value
      .toLowerCase()
      .replace(/[^a-z0-9\u0080-\uFFFF]+/g, '-')
      .replace(/(^-|-$)/g, '')
  );

const normalizeHeadingLabel = (label: string): string => label.replace(/^\d+[.)]\s*/, '').trim();

const getBlockText = (block: any): string =>
  (block?.children || [])
    .map((child: any) => child?.text || '')
    .join('')
    .trim();

const SolutionsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [sanitySolutions, setSanitySolutions] = useState<SanitySolution[]>([]);

  useEffect(() => {
    const normalizedLang = i18n.language?.toLowerCase().startsWith('fr')
      ? 'fr'
      : i18n.language?.toLowerCase().startsWith('zh')
      ? 'zh'
      : 'en';
    const query = `*[_type == "solution" && (language == $lang || language == "en" || !defined(language))] | order(slug.current asc){
      _id,
      "slug": slug.current,
      language,
      headingPairs,
      body
    }`;

    client
      .fetch(query, { lang: normalizedLang })
      .then((data: SanitySolution[]) => {
        if (!Array.isArray(data)) {
          setSanitySolutions([]);
          return;
        }
        const bestBySlug = new Map<string, SanitySolution>();
        for (const item of data) {
          const key = item?.slug;
          if (!key) continue;
          const existing = bestBySlug.get(key);
          if (!existing) {
            bestBySlug.set(key, item);
            continue;
          }
          const itemRank = item.language === normalizedLang ? 3 : item.language === 'en' ? 2 : 1;
          const existingRank =
            existing.language === normalizedLang ? 3 : existing.language === 'en' ? 2 : 1;
          if (itemRank > existingRank) {
            bestBySlug.set(key, item);
          }
        }
        setSanitySolutions(Array.from(bestBySlug.values()));
      })
      .catch(() => setSanitySolutions([]));
  }, [i18n.language]);

  const subCategoryMap = useMemo(() => {
    const map: Record<string, { label: string; href: string }[]> = {};

    for (const solution of sanitySolutions) {
      if (!solution?._id || !solution?.slug) continue;
      const links: { label: string; href: string }[] = [];

      if (Array.isArray(solution.headingPairs) && solution.headingPairs.length > 0) {
        for (const item of solution.headingPairs) {
          if (!item?.label || !item?.anchor) continue;
          links.push({
            label: item.label,
            href: `/solutions/${solution.slug}?sid=${encodeURIComponent(solution._id)}#${item.anchor}`,
          });
        }
      } else {
        const h2Blocks = Array.isArray(solution.body)
          ? solution.body.filter((block: any) => block?._type === 'block' && block?.style === 'h2')
          : [];
        for (const block of h2Blocks) {
          const label = getBlockText(block);
          if (!label) continue;
          const generatedAnchor = slugify(normalizeHeadingLabel(label)) || `section-${solution._id}`;
          links.push({
            label,
            href: `/solutions/${solution.slug}?sid=${encodeURIComponent(solution._id)}#${generatedAnchor}`,
          });
        }
      }

      if (links.length > 0) {
        map[solution.slug] = links;
      }
    }

    return map;
  }, [sanitySolutions]);

  useEffect(() => {
    if (!location.hash) return;

    const targetId = decodeURIComponent(location.hash.replace('#', ''));
    if (!targetId) return;

    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) return false;

      const navbarOffset = 110;
      const y = target.getBoundingClientRect().top + window.scrollY - navbarOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      return true;
    };

    // Retry a few times to handle late layout shifts (images/content hydration/smooth-scroll init).
    let attempts = 0;
    const maxAttempts = 8;
    const retryMs = 120;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      const didScroll = scrollToTarget();
      if (didScroll && attempts >= 2) {
        window.clearInterval(intervalId);
      } else if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, retryMs);

    return () => window.clearInterval(intervalId);
  }, [location.hash]);

  return (
    <main className="pt-[72px]">
      <SEO
        title="Solutions"
        description="Explore integrated security, fire safety, HVAC, solar, and software solutions delivered by Cool & Care across the Democratic Republic of the Congo."
        keywords="technology solutions DRC, integrated security solutions, HVAC systems, fire safety systems, solar solutions, software solutions Kinshasa"
      />
      <BreadcrumbSchema items={[{ name: 'Solutions', url: '/solutions' }]} />
      <PageHero
        eyebrow={t('solutionsPageSection.hero.eyebrow')}
        title={t('solutionsPageSection.hero.title')}
        description={t('solutionsPageSection.hero.description')}
        typedTitle
      />

      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 space-y-32">
          {ourSolutionData.map((item, index) => {
            const textFirst = index % 2 === 0;
            return (
              <SolutionDetailCard
                key={item.id}
                item={item}
                textFirst={textFirst}
                subCategoryLinks={subCategoryMap[item.slug]}
              />
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default SolutionsPage;
